import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ApiService } from '../../core/api.service';
import { I18nService } from '../../core/i18n.service';
import { PaginationComponent } from '../../core/pagination.component';
import { LocalCurrencyPipe, LocalDatePipe, LocalNumberPipe, TranslatePipe } from '../../core/translate.pipe';

type ReportKey = string;
type Period = 'month' | 'year' | 'range' | 'all';

interface ReportOption {
  key: ReportKey;
  title: string;
  description: string;
}

interface ReportColumn {
  key: string;
  label: string;
  kind: 'text' | 'number' | 'currency';
}

interface CustomReportSource {
  key: string;
  title: string;
  fields: ReportColumn[];
}

interface FormalReport {
  organization: string;
  title: string;
  period_label: string;
  generated_at: string;
  footer_lines: string[];
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  summary: Array<{ label: string; value: number; kind: 'number' | 'currency' }>;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, LocalCurrencyPipe, LocalDatePipe, LocalNumberPipe, PaginationComponent, TranslatePipe],
  template: `
    <section class="page-header report-page-header">
      <div>
        <h1>{{ 'Reports' | t }}</h1>
        <p>{{ 'Generate standard reports or build a custom report using fields grouped by application page.' | t }}</p>
      </div>
    </section>

    <section class="report-selector">
      <button
        type="button"
        class="report-choice"
        *ngFor="let option of reportOptions"
        [class.active]="activeReport === option.key"
        (click)="selectReport(option.key)">
        <strong>{{ option.title | t }}</strong>
        <span>{{ option.description | t }}</span>
      </button>
    </section>

    <section class="panel report-controls">
      <div class="report-filter-fields" *ngIf="!isCustomReport">
        <label *ngIf="supportsPeriod">
          {{ 'reports.reportingPeriod' | t }}
          <select [(ngModel)]="period">
            <option value="month">{{ 'common.monthYear' | t }}</option>
            <option value="year">{{ 'common.yearTotal' | t }}</option>
            <option value="range">{{ 'common.dateRange' | t }}</option>
            <option value="all">{{ 'common.allTime' | t }}</option>
          </select>
        </label>
        <label *ngIf="supportsMonth">
          {{ 'common.month' | t }}
          <select [(ngModel)]="month">
            <option *ngFor="let item of months" [ngValue]="item.value">{{ item.labelKey | t }}</option>
          </select>
        </label>
        <label *ngIf="showYear">
          {{ 'common.year' | t }}
          <select [(ngModel)]="year">
            <option *ngFor="let item of years" [ngValue]="item">{{ item }}</option>
          </select>
        </label>
        <label *ngIf="isCumulativeReport || (supportsPeriod && period === 'range')">
          {{ 'reports.fromDate' | t }}
          <input type="date" [(ngModel)]="startDate">
        </label>
        <label *ngIf="isCumulativeReport || (supportsPeriod && period === 'range')">
          {{ 'reports.toDate' | t }}
          <input type="date" [(ngModel)]="endDate">
        </label>
      </div>

      <div class="custom-report-builder" *ngIf="isCustomReport">
        <div class="custom-report-source-row">
          <label>
            {{ 'Application Page / Data Source' | t }}
            <select [(ngModel)]="customSourceKey" (change)="changeCustomSource()">
              <option *ngFor="let source of customSources" [value]="source.key">{{ source.title | t }}</option>
            </select>
          </label>
          <label>
            {{ 'reports.fromDate' | t }}
            <input type="date" [(ngModel)]="startDate">
          </label>
          <label>
            {{ 'reports.toDate' | t }}
            <input type="date" [(ngModel)]="endDate">
          </label>
        </div>
        <div class="custom-field-heading">
          <strong>{{ 'Select Report Fields' | t }}</strong>
          <span>{{ selectedFields.length }} {{ 'fields selected' | t }}</span>
        </div>
        <div class="custom-field-grid">
          <label *ngFor="let field of activeCustomSource?.fields">
            <input type="checkbox" [checked]="isFieldSelected(field.key)" (change)="toggleField(field.key, $event)">
            <span>{{ field.label | t }}</span>
          </label>
        </div>
      </div>

      <div class="report-control-actions">
        <button type="button" (click)="load()" [disabled]="loading">{{ 'common.generateReport' | t }}</button>
        <button type="button" class="download excel" (click)="download('xlsx')" [disabled]="loading || !report">{{ 'common.downloadExcel' | t }}</button>
        <button type="button" class="download pdf" (click)="download('pdf')" [disabled]="loading || !report">{{ 'common.downloadPdf' | t }}</button>
      </div>
    </section>

    <p class="error" *ngIf="error">{{ error }}</p>
    <p class="loading-text" *ngIf="loading">{{ 'reports.preparing' | t }}</p>

    <article class="official-report" *ngIf="report">
      <header class="official-report-header">
        <img src="assets/ofs-logo.png" alt="Odisha Fire and Emergency Services logo">
        <div>
          <h2>{{ report.organization | t }}</h2>
          <p>{{ 'reports.procurementCell' | t }}</p>
        </div>
      </header>
      <section class="official-report-title">
        <h3>{{ report.title | t }}</h3>
        <p>{{ 'reports.reportingPeriod' | t }}: {{ report.period_label | t }}</p>
        <p>{{ 'reports.generatedOn' | t }}: {{ report.generated_at | ldate }}</p>
      </section>

      <section class="report-summary-cards">
        <div *ngFor="let item of report.summary">
          <span>{{ item.label | t }}</span>
          <strong *ngIf="item.kind === 'currency'">{{ item.value | lcurrency:'INR':'1.2-2' }}</strong>
          <strong *ngIf="item.kind !== 'currency'">{{ item.value | ln:'1.0-0' }}</strong>
        </div>
      </section>

      <div class="report-table-scroll">
        <table class="formal-report-table">
          <thead>
            <tr><th *ngFor="let column of report.columns">{{ column.label | t }}</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of pagedRows">
              <td *ngFor="let column of report.columns" [class.numeric-cell]="column.kind !== 'text'">
                <ng-container [ngSwitch]="column.kind">
                  <span *ngSwitchCase="'currency'">{{ isBlank(row[column.key]) ? '-' : (numberValue(row[column.key]) | lcurrency:'INR':'1.2-2') }}</span>
                  <span *ngSwitchCase="'number'">{{ isBlank(row[column.key]) ? '-' : (numberValue(row[column.key]) | ln:'1.0-0') }}</span>
                  <span *ngSwitchDefault>{{ textValue(row[column.key]) | t }}</span>
                </ng-container>
              </td>
            </tr>
            <tr *ngIf="!report.rows.length">
              <td class="report-empty" [attr.colspan]="report.columns.length">{{ 'reports.noRecords' | t }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <app-pagination
        [total]="report.rows.length"
        [page]="page"
        [pageSize]="pageSize"
        [pageSizes]="pageSizes"
        (pageChange)="goToPage($event)"
        (pageSizeChange)="changePageSize($event)">
      </app-pagination>

      <footer class="official-report-footer">
        <span>{{ report.footer_lines[0] | t }}</span>
        <span>{{ report.footer_lines[1] | t }}</span>
      </footer>
    </article>
  `
})
export class ReportsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly i18n = inject(I18nService);
  private readonly today = new Date();

  readonly reportOptions: ReportOption[] = [
    { key: 'custom', title: 'Custom Report Builder', description: 'Select a page, date range and multiple fields' },
    { key: 'summary', title: '1. Summary Report', description: 'Module-wise counts, status and values' },
    { key: 'daily', title: '2. Daily Report', description: 'Daily procurement status based on the approved reference format' },
    { key: 'monthly', title: '3. Monthly Report', description: 'Month-wise procurement movement within a year' },
    { key: 'vendor-payment-status', title: '4. Vendor Payment Status / Report', description: 'Vendor invoices, paid and outstanding amounts' },
    { key: 'budget-utilization', title: '5. Budget Utilization Report', description: 'Allocation, utilization and available balance' },
    { key: 'program-expenditure', title: '6. Programme Expenditure Report', description: 'Programme head allocation and expenditure' },
    { key: 'cumulative-activity', title: '7. Cumulative Activity Report', description: 'Day-wise lifecycle activity comparison with running totals' }
  ];

  // Retained for future re-enablement. These reports are intentionally not rendered on the current Reports page.
  readonly legacyReportOptions: ReportOption[] = [
    { key: 'establishment', title: 'Establishment-wise Record', description: 'Requisition distribution by establishment' },
    { key: 'yearly', title: 'Yearly Report', description: 'Year-on-year procurement totals' },
    { key: 'gem-register', title: 'GeM Procurement Register', description: 'GeM bids, contracts and order values' },
    { key: 'gem-order-status', title: 'GeM Order Status Report', description: 'Delivery and payment status for GeM orders' },
    { key: 'e-tender-register', title: 'e-Tender Register', description: 'Odisha e-Procurement tender lifecycle' },
    { key: 'technical-evaluation-status', title: 'Technical Evaluation Status', description: 'Technical scrutiny status for e-Tenders' },
    { key: 'financial-evaluation-status', title: 'Financial Evaluation Status', description: 'Financial evaluation and award status' },
    { key: 'global-tender-register', title: 'Global Tender Register', description: 'International bidding cases' },
    { key: 'foreign-vendor-procurement', title: 'Foreign Vendor Procurement', description: 'Foreign currency and INR procurement values' },
    { key: 'lpc-register', title: 'LPC Register', description: 'Local Purchase Committee cases' },
    { key: 'lpc-recommendation', title: 'LPC Recommendation Report', description: 'Committee recommendations and comparative statements' },
    { key: 'open-market-register', title: 'Open Market Register', description: 'Open market procurement cases' },
    { key: 'open-market-justification', title: 'Open Market Justification', description: 'Justification and market survey details' },
    { key: 'other-procurement-register', title: 'Other Procurement Register', description: 'Manually entered and other procurement cases' },
    { key: 'emd-register', title: 'EMD Register', description: 'EMD instruments and refund status' },
    { key: 'emd-pending-refund', title: 'EMD Pending Refund', description: 'EMDs pending return or refund' },
    { key: 'emd-forfeiture-register', title: 'EMD Forfeiture Register', description: 'Forfeited EMD records' },
    { key: 'performance-security-register', title: 'Performance Security Register', description: 'PBG and security instruments' },
    { key: 'pbg-expiry-alert', title: 'PBG Expiry Alert', description: 'Performance securities by expiry date' },
    { key: 'contract-expiry-alert', title: 'Contract Expiry Alert', description: 'Purchase orders near completion' },
    { key: 'budget-head-expenditure', title: 'Budget Head-wise Expenditure', description: 'Expenditure against budget heads' },
    { key: 'procurement-value-analysis', title: 'Procurement Value Analysis', description: 'Approved value by procurement mode' },
    { key: 'vendor-procurement-value', title: 'Vendor-wise Procurement Value', description: 'Purchase value by vendor' },
    { key: 'financial-year-summary', title: 'Financial Year-wise Summary', description: 'Financial year procurement movement' },
    { key: 'district-procurement-summary', title: 'District-wise Procurement Summary', description: 'Procurement grouped by district' },
    { key: 'circle-procurement-summary', title: 'Circle-wise Procurement Summary', description: 'Procurement grouped by circle or establishment' },
    { key: 'range-procurement-summary', title: 'Range-wise Procurement Summary', description: 'Procurement grouped by range' },
    { key: 'directorate-dashboard', title: 'Directorate Procurement Dashboard', description: 'Directorate level procurement case status' },
    { key: 'cycle-time-analysis', title: 'Procurement Cycle Time Analysis', description: 'Mode selection and sanction movement' },
    { key: 'procurement-aging-analysis', title: 'Procurement Aging Analysis', description: 'Open procurement case aging' },
    { key: 'top-vendors', title: 'Top Vendors Report', description: 'Vendor value and order status' },
    { key: 'asset-procurement-distribution', title: 'Asset Procurement & Distribution', description: 'Assets/items procured and distributed' }
  ];

  readonly months = [
    { value: 1, labelKey: 'month.1' }, { value: 2, labelKey: 'month.2' }, { value: 3, labelKey: 'month.3' },
    { value: 4, labelKey: 'month.4' }, { value: 5, labelKey: 'month.5' }, { value: 6, labelKey: 'month.6' },
    { value: 7, labelKey: 'month.7' }, { value: 8, labelKey: 'month.8' }, { value: 9, labelKey: 'month.9' },
    { value: 10, labelKey: 'month.10' }, { value: 11, labelKey: 'month.11' }, { value: 12, labelKey: 'month.12' }
  ];
  readonly years = Array.from({ length: 12 }, (_, index) => this.today.getFullYear() - index);

  activeReport: ReportKey = 'summary';
  period: Period = 'month';
  month = this.today.getMonth() + 1;
  year = this.today.getFullYear();
  startDate = '';
  endDate = '';
  customSources: CustomReportSource[] = [];
  customSourceKey = '';
  selectedFields: string[] = [];
  report?: FormalReport;
  readonly pageSizes = [10, 25, 50, 100];
  page = 1;
  pageSize = 10;
  loading = false;
  error = '';

  ngOnInit(): void {
    this.loadCustomMetadata();
    this.load();
  }

  get isCustomReport(): boolean {
    return this.activeReport === 'custom';
  }

  get isCumulativeReport(): boolean {
    return this.activeReport === 'cumulative-activity';
  }

  get activeCustomSource(): CustomReportSource | undefined {
    return this.customSources.find((source) => source.key === this.customSourceKey);
  }

  get supportsPeriod(): boolean {
    return !this.isCustomReport && !this.isCumulativeReport && this.activeReport !== 'monthly';
  }

  get supportsMonth(): boolean {
    return this.supportsPeriod && this.period === 'month';
  }

  get showYear(): boolean {
    return this.activeReport === 'monthly' || (this.supportsPeriod && (this.period === 'month' || this.period === 'year'));
  }

  selectReport(reportKey: ReportKey): void {
    this.activeReport = reportKey;
    this.report = undefined;
    this.page = 1;
    if (reportKey === 'custom') {
      if (!this.customSourceKey && this.customSources.length) this.setCustomSource(this.customSources[0]);
      return;
    }
    if (reportKey === 'cumulative-activity') {
      this.period = 'range';
      this.setLastSevenDays();
    } else if (reportKey === 'monthly') this.period = 'year';
    else if (reportKey === 'daily') {
      this.period = 'range';
      const today = this.today.toISOString().slice(0, 10);
      this.startDate = today;
      this.endDate = today;
    } else {
      this.period = 'month';
    }
    this.load();
  }

  changeCustomSource(): void {
    const source = this.activeCustomSource;
    if (source) this.setCustomSource(source);
  }

  isFieldSelected(key: string): boolean {
    return this.selectedFields.includes(key);
  }

  toggleField(key: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedFields = checked
      ? [...this.selectedFields, key]
      : this.selectedFields.filter((field) => field !== key);
  }

  load(): void {
    if (!this.validateRange()) return;
    if (this.isCustomReport && (!this.customSourceKey || !this.selectedFields.length)) {
      this.error = this.i18n.t('Select a data source and at least one field.');
      return;
    }
    this.loading = true;
    const request = this.isCustomReport
      ? this.api.customReport(this.customSourceKey, this.selectedFields, this.startDate, this.endDate)
      : this.api.formalReport(this.activeReport, this.filter());
    request.subscribe({
      next: (data) => {
        this.report = data as FormalReport;
        this.page = 1;
        this.error = '';
        this.loading = false;
      },
      error: (response) => {
        this.error = response?.error?.detail ? this.i18n.t(response.error.detail) : this.i18n.t('reports.failedGenerate');
        this.loading = false;
      }
    });
  }

  download(format: 'xlsx' | 'pdf'): void {
    if (!this.validateRange() || !this.report) return;
    this.loading = true;
    const request = this.isCustomReport
      ? this.api.exportCustomReport(this.customSourceKey, this.selectedFields, format, this.startDate, this.endDate)
      : this.api.exportReport(this.activeReport, format, this.filter());
    request.subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.error = this.i18n.t('reports.emptyDownload');
          this.loading = false;
          return;
        }
        const fallback = `${this.activeReport}-report.${format}`;
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="([^"]+)"/);
        this.saveBlob(blob, match?.[1] || fallback);
        this.error = '';
        this.loading = false;
      },
      error: (response) => {
        this.error = response?.error?.detail
          ? this.i18n.t(response.error.detail)
          : this.i18n.t('reports.failedDownload', { format: format.toUpperCase() });
        this.loading = false;
      }
    });
  }

  numberValue(value: unknown): number {
    return Number(value || 0);
  }

  isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  textValue(value: unknown): string {
    return value === undefined || value === null || value === '' ? '-' : String(value);
  }

  get reportTotalPages(): number {
    return Math.max(1, Math.ceil((this.report?.rows.length || 0) / this.pageSize));
  }

  get pagedRows(): Record<string, unknown>[] {
    const rows = this.report?.rows || [];
    const start = (this.page - 1) * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  changePageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1;
  }

  goToPage(page: number): void {
    this.page = Math.min(Math.max(1, page), this.reportTotalPages);
  }

  private loadCustomMetadata(): void {
    this.api.customReportMetadata().subscribe({
      next: (data) => {
        this.customSources = (data['sources'] || []) as CustomReportSource[];
        if (this.customSources.length) this.setCustomSource(this.customSources[0]);
      },
      error: () => this.error = this.i18n.t('Unable to load custom report fields.')
    });
  }

  private setCustomSource(source: CustomReportSource): void {
    this.customSourceKey = source.key;
    this.selectedFields = source.fields.slice(0, 6).map((field) => field.key);
    this.report = undefined;
  }

  private filter(): { period: string; month?: number; year?: number; startDate?: string; endDate?: string } {
    if (this.activeReport === 'monthly') return { period: 'year', year: this.year };
    if (this.isCumulativeReport) return { period: 'range', startDate: this.startDate, endDate: this.endDate };
    return {
      period: this.period,
      month: this.period === 'month' ? this.month : undefined,
      year: this.period === 'month' || this.period === 'year' ? this.year : undefined,
      startDate: this.period === 'range' ? this.startDate : undefined,
      endDate: this.period === 'range' ? this.endDate : undefined
    };
  }

  private validateRange(): boolean {
    const usesRange = this.isCustomReport || this.isCumulativeReport || this.period === 'range';
    if (usesRange && this.startDate && this.endDate && this.startDate > this.endDate) {
      this.error = this.i18n.t('dashboard.fromAfterTo');
      return false;
    }
    return true;
  }

  private setLastSevenDays(): void {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    this.startDate = this.localDateValue(start);
    this.endDate = this.localDateValue(end);
  }

  private localDateValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
